import { useEffect, useState } from 'react';
import {
  equalManualRubles,
  formatExpenseRub,
  isManualRubSumComplete,
  isManualShareSumComplete,
  manualRubSum,
  manualShareSum,
  parseRubInput,
  rublesFromShares,
  sharesFromRubles,
  toggleManualRub,
  toggleManualShare,
  type ManualSplitUnit,
} from '../../utils/expenseSplit';
import { formatOwnershipShareSum } from '../../utils/ownershipShare';
import './ManualShareEditor.css';

type Owner = { user_id: number; name: string };

type Props = {
  owners: Owner[];
  amount: number | null;
  manualShares: Record<number, string>;
  onManualSharesChange: (value: Record<number, string>) => void;
  noOwnersHint?: string;
};

const ManualShareEditor = ({
  owners,
  amount,
  manualShares,
  onManualSharesChange,
  noOwnersHint = 'Нет собственников на эту дату',
}: Props) => {
  const amountOk = amount != null && amount > 0;
  const [unit, setUnit] = useState<ManualSplitUnit>('rub');
  const [rubles, setRubles] = useState<Record<number, string>>(() =>
    rublesFromShares(manualShares, amountOk ? amount : 0)
  );
  const total = amountOk ? amount : 0;
  const shareSum = manualShareSum(manualShares);
  const shareSumOk = isManualShareSumComplete(manualShares);
  const rubSum = manualRubSum(rubles);
  const rubSumOk = isManualRubSumComplete(rubles, total);

  useEffect(() => {
    if (unit !== 'rub' || !amountOk) return;
    const ids = Object.keys(rubles).map(Number);
    if (ids.length === 0) return;
    if (!ids.every((id) => !(rubles[id] || '').trim())) return;
    const next = equalManualRubles(ids, amount);
    setRubles(next);
    onManualSharesChange(sharesFromRubles(next, amount));
  }, [amount, amountOk, onManualSharesChange, rubles, unit]);

  const setUnitShare = () => {
    setUnit('share');
  };

  const setUnitRub = () => {
    setRubles(rublesFromShares(manualShares, total));
    setUnit('rub');
  };

  const onToggle = (userId: number, checked: boolean) => {
    if (unit === 'share') {
      onManualSharesChange(toggleManualShare(manualShares, userId, checked));
      return;
    }
    const next = toggleManualRub(rubles, userId, checked, total);
    setRubles(next);
    onManualSharesChange(sharesFromRubles(next, total));
  };

  const onShareText = (userId: number, value: string) => {
    onManualSharesChange({
      ...manualShares,
      [userId]: value,
    });
  };

  const onRubText = (userId: number, value: string) => {
    const next = { ...rubles, [userId]: value };
    setRubles(next);
    onManualSharesChange(sharesFromRubles(next, total));
  };

  return (
    <>
      <div className="manual-unit" role="group" aria-label="Чем задать доли">
        <button
          type="button"
          className={unit === 'rub' ? 'is-active' : undefined}
          onClick={setUnitRub}
        >
          Рубли
        </button>
        <button
          type="button"
          className={unit === 'share' ? 'is-active' : undefined}
          onClick={setUnitShare}
        >
          Доли
        </button>
      </div>
      {unit === 'rub' && !amountOk ? (
        <p className="manual-share-hint">Сначала укажите сумму расхода</p>
      ) : null}
      {owners.length === 0 ? (
        <p className="manual-share-hint">{noOwnersHint}</p>
      ) : (
        <ul className="manual-share-list">
          {owners.map((owner) => {
            const source = unit === 'rub' ? rubles : manualShares;
            const checked = Object.prototype.hasOwnProperty.call(
              source,
              owner.user_id
            );
            return (
              <li key={owner.user_id} className="manual-share-row">
                <label>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) =>
                      onToggle(owner.user_id, event.target.checked)
                    }
                  />
                  {owner.name}
                </label>
                <span
                  className={
                    unit === 'rub'
                      ? 'manual-share-slot is-rub'
                      : 'manual-share-slot'
                  }
                >
                  {checked ? (
                    <input
                      type="text"
                      inputMode={unit === 'rub' ? 'decimal' : 'text'}
                      placeholder={unit === 'rub' ? '0' : '1/2'}
                      value={
                        unit === 'rub'
                          ? rubles[owner.user_id] ?? ''
                          : manualShares[owner.user_id] ?? ''
                      }
                      onChange={(event) =>
                        unit === 'rub'
                          ? onRubText(owner.user_id, event.target.value)
                          : onShareText(owner.user_id, event.target.value)
                      }
                      aria-label={
                        unit === 'rub'
                          ? `Сумма ${owner.name}`
                          : `Доля ${owner.name}`
                      }
                    />
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {unit === 'share' && Object.keys(manualShares).length > 0 ? (
        <p
          className={
            shareSumOk ? 'manual-share-hint' : 'manual-share-hint is-warn'
          }
        >
          Сумма: {formatOwnershipShareSum(shareSum)}
          {shareSumOk ? '' : ' — должна быть равна 1'}
        </p>
      ) : null}
      {unit === 'rub' && Object.keys(rubles).length > 0 ? (
        <p
          className={
            rubSumOk ? 'manual-share-hint' : 'manual-share-hint is-warn'
          }
        >
          Сумма:{' '}
          {Number.isFinite(rubSum)
            ? formatExpenseRub(rubSum)
            : '—'}
          {amountOk
            ? rubSumOk
              ? ''
              : ` — должна быть ${formatExpenseRub(amount)}`
            : ''}
        </p>
      ) : null}
    </>
  );
};

export default ManualShareEditor;

export const parseCostAmount = (raw: string): number | null => {
  const parsed = parseRubInput(raw);
  if (parsed === 'invalid' || parsed == null || parsed <= 0) return null;
  return parsed;
};
